import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Calendar, Newspaper } from "lucide-react";
import { api, fileUrl, mediaUrl } from "@/lib/api";
import { LGAS } from "@/lib/data";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function NewsGrid({ items, onOpen }) {
  if (!items.length) return <div className="py-20 text-center text-zinc-500">No news posted yet.</div>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((n, i) => (
        <motion.button key={n.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
          onClick={() => onOpen(n)} className="text-left rounded-2xl border border-white/10 bg-[#131519] overflow-hidden hover:border-[#E6197E]/50 transition-colors">
          {n.image && <div className="aspect-video bg-zinc-800 overflow-hidden"><img src={mediaUrl(n.image)} alt="" loading="lazy" className="w-full h-full object-cover" /></div>}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${n.scope === "national" ? "bg-[#E6197E]/20 text-[#E6197E]" : "bg-amber-600/20 text-amber-400"}`}>{n.scope === "national" ? "National" : n.lga}</span>
            </div>
            <h3 className="font-heading font-semibold text-lg leading-tight">{n.title}</h3>
            <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{n.content}</p>
            <div className="mt-4 text-[11px] text-zinc-500">By {n.author_name} · {new Date(n.created_at).toLocaleDateString()}</div>
          </div>
        </motion.button>
      ))}
    </div>
  );
}

export default function News() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lga, setLga] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/news").then((r) => setAll(r.data)).finally(() => setLoading(false)); }, []);

  const national = all.filter((n) => n.scope === "national");
  const lgaNews = all.filter((n) => n.scope === "lga" && (lga === "all" || n.lga === lga));

  return (
    <main className="pt-28 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="overline-label mb-3">Latest Updates</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight flex items-center gap-3"><Newspaper className="w-8 h-8 text-[#E6197E]" /> News</h1>

        {loading ? <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E6197E]" /></div> : (
          <Tabs defaultValue="all" className="mt-10">
            <TabsList className="bg-[#131519] border border-white/10">
              <TabsTrigger value="all" data-testid="news-tab-all">All</TabsTrigger>
              <TabsTrigger value="national" data-testid="news-tab-national">National</TabsTrigger>
              <TabsTrigger value="lga" data-testid="news-tab-lga">By LGA</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="mt-8"><NewsGrid items={all} onOpen={setActive} /></TabsContent>
            <TabsContent value="national" className="mt-8"><NewsGrid items={national} onOpen={setActive} /></TabsContent>
            <TabsContent value="lga" className="mt-8">
              <div className="mb-6 max-w-xs">
                <Select value={lga} onValueChange={setLga}>
                  <SelectTrigger className="bg-[#131519] border-white/10" data-testid="news-lga-selector"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All LGAs</SelectItem>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <NewsGrid items={lgaNews} onOpen={setActive} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-[#131519] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          {active && (<>
            <DialogHeader><DialogTitle className="font-heading text-2xl">{active.title}</DialogTitle></DialogHeader>
            <div className="text-xs text-zinc-500 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> {new Date(active.created_at).toLocaleDateString()} · {active.scope === "national" ? "National" : active.lga} · By {active.author_name}</div>
            {active.image && <img src={mediaUrl(active.image)} alt="" className="rounded-xl w-full mt-2" />}
            <p className="text-zinc-300 whitespace-pre-wrap leading-relaxed">{active.content}</p>
          </>)}
        </DialogContent>
      </Dialog>
    </main>
  );
}
